import type { SxProps, Theme } from "@mui/material";
import { Checkbox, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { parseISO } from "date-fns";
import NumberTextField from "@entrilia/shared/components/inputs/NumberTextField";
import { formatDate, formatDateOnly } from "@entrilia/shared/utilities/dateUtils";
import { convertToNumber } from "@entrilia/shared/utilities/numberHelper";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { EntityFieldConfiguration, EntityFieldType, FieldAttribute } from "../../../api/types/objectTypes";
import SelectFieldEdit from "./SelectFieldEdit";
import UrlFieldEdit from "./UrlFieldEdit";

interface Props {
  value: unknown;
  fieldName: string;
  fieldType: EntityFieldType;
  fieldConfiguration?: EntityFieldConfiguration;
  fieldAttributes?: FieldAttribute[];
  onChange: (fieldName: string, value: unknown) => void;
  hasError?: boolean;
  validationError?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const FieldEdit = ({
  value,
  fieldName,
  fieldType,
  fieldConfiguration,
  fieldAttributes,
  onChange,
  hasError,
  validationError,
  disabled,
  sx,
}: Props) => {
  const isFieldReadOnly = disabled || fieldAttributes?.includes("ReadOnly");
  const showError = hasError || validationError !== undefined;

  if (fieldType === "Number" || fieldType === "Money" || fieldType === "Percent") {
    return (
      <NumberTextField
        fullWidth
        numberValue={convertToNumber(value)}
        onNumberChange={(newValue) => onChange(fieldName, newValue)}
        disabled={isFieldReadOnly}
        error={showError}
        helperText={validationError}
        sx={sx}
      />
    );
  }

  if (fieldType === "Date" || fieldType === "DateTime") {
    const dateValue = typeof value === "string" ? parseISO(value) : null;
    return (
      <DatePicker
        value={dateValue}
        disabled={isFieldReadOnly}
        sx={sx}
        onChange={(newDate) => {
          const newValue = newDate ? (fieldType === "Date" ? formatDateOnly(newDate) : formatDate(newDate)) : undefined;
          onChange(fieldName, newValue);
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            error: showError,
            helperText: validationError,
          },
        }}
      />
    );
  }

  if (fieldType === "Checkbox") {
    return (
      <Checkbox
        checked={typeof value === "boolean" && value}
        onChange={(_, checked) => onChange(fieldName, checked)}
        disabled={isFieldReadOnly}
      />
    );
  }

  if (
    (fieldType === "Select" && fieldConfiguration?.$type === "Select") ||
    (fieldType === "UserDefinedOptionsSelect" && fieldConfiguration?.$type === "UserDefinedOptionsSelect")
  ) {
    return (
      <SelectFieldEdit
        value={value}
        fieldConfiguration={fieldConfiguration}
        onChange={(newValue) => onChange(fieldName, newValue ?? undefined)}
        disabled={isFieldReadOnly}
        hasError={showError}
        helperText={validationError}
        sx={sx}
      />
    );
  }

  if (fieldType === "Url") {
    return (
      <UrlFieldEdit
        value={value}
        onChange={(markdownUrl) => onChange(fieldName, markdownUrl)}
        disabled={isFieldReadOnly}
        hasError={showError}
        validationError={validationError}
        sx={sx}
      />
    );
  }

  // TODO add support for MultiSelect, UserDefinedOptionsMultiSelect, Lookup and MultiLookup when needed

  return (
    <TextField
      fullWidth
      value={stringifyValue(value)}
      onChange={(e) => onChange(fieldName, e.target.value)}
      disabled={isFieldReadOnly}
      error={showError}
      helperText={validationError}
      sx={sx}
    />
  );
};

export default FieldEdit;
